<template>
  <div
    class="connect-cell"
    @click="handleClick"
  >

    <!-- =====================================================
         CHAT
    ====================================================== -->

    <button
      type="button"
      class="chat-button"
      aria-label="Открыть чат"
      @click.stop="goToChat"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7.5 18.2L4.5 20V7C4.5 5.6 5.6 4.5 7 4.5H17C18.4 4.5 19.5 5.6 19.5 7V14.2C19.5 15.6 18.4 16.7 17 16.7H9.5L7.5 18.2Z"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round"
        />

        <path
          d="M8.2 9H15.8"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />

        <path
          d="M8.2 12.2H13.2"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </button>


    <!-- =====================================================
         AVATAR
    ====================================================== -->

    <div
      class="avatar-wrapper"
      :class="{ viewed: isViewed }"
    >

      <div
        v-if="!imageLoaded"
        class="avatar-skeleton"
      ></div>

      <img
        class="avatar"
        :class="{ loaded: imageLoaded }"
        :src="avatarToShow"
        alt="Аватар"
        @load="onImageLoad"
        @error="onImageError"
      />

      <span
        v-if="!isViewed && imageLoaded"
        class="status-dot"
      ></span>

    </div>


    <!-- =====================================================
         USER INFO
    ====================================================== -->

    <div class="user-info">

      <h3 class="name">
        {{ name || "Пользователь" }}
      </h3>

      <p
        class="age"
        :class="{
          'is-company-age': isCompanyMode
        }"
      >
        {{ ageLine }}
      </p>

      <p
        class="specific"
        :class="{
          'is-company': props.companyName,
          'is-specific': !props.companyName
        }"
      >
        {{
          props.companyName
            ? props.companyName
            : formattedSpecifics
        }}
      </p>

    </div>


    <!-- =====================================================
         ACTION
    ====================================================== -->

    <button
      type="button"
      class="connect-button"
      :class="{
        sent: isRequestSent,
        ask: props.isConsultant,
        loading: isRequestLoading
      }"
      :disabled="isRequestLoading"
      @click.stop="handleButtonClick"
    >

      <span
        v-if="isRequestLoading"
        class="btn-spinner"
      ></span>

      <template v-else>

        <!-- CONSULTANT -->

        <svg
          v-if="props.isConsultant"
          class="button-icon"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 14.8L3.4 16V6.2C3.4 5.2 4.2 4.4 5.2 4.4H14.8C15.8 4.4 16.6 5.2 16.6 6.2V12.7C16.6 13.7 15.8 14.5 14.8 14.5H6L5 14.8Z"
            stroke="currentColor"
            stroke-width="1.45"
            stroke-linejoin="round"
          />
        </svg>


        <!-- SENT -->

        <svg
          v-else-if="isRequestSent"
          class="button-icon"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4.8 10.1L8.1 13.3L15.4 6.7"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>


        <!-- CONNECT -->

        <svg
          v-else
          class="button-icon"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="7.2"
            cy="6.8"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.45"
          />

          <path
            d="M3.7 14.8C4.2 12.1 5.3 10.9 7.2 10.9C9.1 10.9 10.2 12.1 10.7 14.8"
            stroke="currentColor"
            stroke-width="1.45"
            stroke-linecap="round"
          />

          <path
            d="M14.4 7.1V12.1"
            stroke="currentColor"
            stroke-width="1.45"
            stroke-linecap="round"
          />

          <path
            d="M11.9 9.6H16.9"
            stroke="currentColor"
            stroke-width="1.45"
            stroke-linecap="round"
          />
        </svg>


        <span>
          {{
            props.isConsultant
              ? "Задать вопрос"
              : (
                  isRequestSent
                    ? "Отправлено"
                    : "Коннект"
                )
          }}
        </span>

      </template>

    </button>

  </div>
</template>


<script setup>

import {
  ref,
  computed,
  onMounted,
  watch
} from "vue";

import {
  useRouter
} from "vue-router";

import ConnectPlusService
  from "../api/ConnectPlusService.js";


const emit = defineEmits([
  "askQuestion"
]);


const props = defineProps({

  userId: String,

  name: String,

  avatar: String,

  age: Number,

  specifics: String,

  isConsultant: Boolean,

  isRemote: Boolean,

  isRequested: {
    type: Boolean,
    default: false
  },

  companyName: {
    type: String,
    default: ""
  }

});


const router =
  useRouter();


/* ============================================================
   IMAGE
============================================================ */

const imageLoaded =
  ref(false);


const onImageLoad = () => {
  imageLoaded.value = true;
};


const onImageError = event => {

  event.target.src =
    new URL(
      "../assets/default-avatar.png",
      import.meta.url
    ).href;

  imageLoaded.value = true;
};


const avatarToShow =
  computed(() => {

    return (
      props.avatar &&
      props.avatar.trim() !== ""
    )
      ? props.avatar
      : new URL(
          "../assets/default-avatar.png",
          import.meta.url
        ).href;

  });


/* ============================================================
   REQUEST
============================================================ */

const isRequestLoading =
  ref(false);


const isRequestSent =
  ref(props.isRequested);


watch(
  () => props.isRequested,
  value => {
    isRequestSent.value =
      value;
  }
);


const handleButtonClick =
  async () => {

    if (props.isConsultant) {

      emit(
        "askQuestion",
        props.userId
      );

      return;
    }


    if (isRequestLoading.value) {
      return;
    }


    const deleting =
      isRequestSent.value;


    isRequestLoading.value =
      true;


    try {

      const success =
        await ConnectPlusService
          .sendFriendRequest(
            props.userId,
            deleting
          );


      if (success) {

        isRequestSent.value =
          !isRequestSent.value;

      }

    } catch (error) {

      console.error(
        "❌ Ошибка изменения коннекта:",
        error
      );

    } finally {

      isRequestLoading.value =
        false;

    }

  };


/* ============================================================
   CHAT
============================================================ */

const isMobile = () =>
  window.innerWidth <= 768;


const goToChat = () => {

  router.push({

    path:
      isMobile()
        ? "/m-dialogs"
        : "/dialogs",

    query: {

      ghost_user_id:
        props.userId,

      name:
        props.name,

      image:
        props.avatar,

      specifics:
        props.specifics || "",

      dialogSource:
        "connect"

    }

  });

};


/* ============================================================
   PROFILE CLICK
============================================================ */

const isViewed =
  ref(false);


const handleClick = () => {

  const viewedUsers =
    JSON.parse(
      localStorage.getItem(
        "viewed_users"
      ) || "[]"
    );


  if (
    !viewedUsers.includes(
      props.userId
    )
  ) {

    viewedUsers.push(
      props.userId
    );


    localStorage.setItem(
      "viewed_users",
      JSON.stringify(
        viewedUsers
      )
    );

  }


  router.push(
    `/user/${props.userId}`
  );

};


/* ============================================================
   COMPUTED
============================================================ */

const isCompanyMode =
  computed(() =>
    !!props.companyName
  );


const formattedSpecificWithCompany =
  computed(() => {

    if (!props.specifics) {
      return "Специальность не указана";
    }

    return `${props.specifics} в`;

  });


const formattedAge =
  computed(() => {

    if (!props.age) {
      return "Возраст не указан";
    }


    const lastDigit =
      props.age % 10;

    const lastTwo =
      props.age % 100;


    if (
      lastTwo >= 11 &&
      lastTwo <= 14
    ) {
      return `${props.age} лет`;
    }


    if (lastDigit === 1) {
      return `${props.age} год`;
    }


    if (
      lastDigit >= 2 &&
      lastDigit <= 4
    ) {
      return `${props.age} года`;
    }


    return `${props.age} лет`;

  });


const ageLine =
  computed(() => {

    if (props.companyName) {
      return formattedSpecificWithCompany.value;
    }

    return formattedAge.value;

  });


const formattedSpecifics =
  computed(() => {

    return (
      props.specifics &&
      props.specifics.trim() !== ""
    )
      ? props.specifics
      : "Специальность не указана";

  });


/* ============================================================
   MOUNT
============================================================ */

onMounted(() => {

  const viewedUsers =
    JSON.parse(
      localStorage.getItem(
        "viewed_users"
      ) || "[]"
    );


  isViewed.value =
    viewedUsers.includes(
      props.userId
    );

});

</script>


<style scoped>

/* ============================================================
   CARD
============================================================ */

.connect-cell {
  position: relative;

  width: 200px;
  height: 240px;

  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding:
    15px
    13px
    12px;

  overflow: hidden;

  color: #edf0f4;

  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.065);

  border-radius: 20px;

  box-shadow:
    0
    12px
    30px
    rgba(0, 0, 0, 0.15);

  cursor: pointer;

  text-align: center;

  user-select: none;

  transition:
    transform 0.20s ease,
    border-color 0.20s ease,
    box-shadow 0.20s ease;
}


.connect-cell:hover {
  transform:
    translateY(-3px);

  border-color:
    rgba(255, 255, 255, 0.105);

  box-shadow:
    0
    18px
    38px
    rgba(0, 0, 0, 0.24);
}


.connect-cell:active {
  transform:
    translateY(-1px)
    scale(0.992);
}


/* ============================================================
   LIGHT
============================================================ */

html[data-theme="light"]
.connect-cell {
  color: #31363d;

  background:
    #ffffff;

  border-color:
    rgba(20, 35, 55, 0.075);

  box-shadow:
    0
    12px
    30px
    rgba(15, 23, 42, 0.045);
}


html[data-theme="light"]
.connect-cell:hover {
  border-color:
    rgba(60, 100, 155, 0.13);

  box-shadow:
    0
    17px
    36px
    rgba(15, 23, 42, 0.08);
}


/* ============================================================
   CHAT
============================================================ */

.chat-button {
  position: absolute;

  top: 12px;
  right: 12px;

  z-index: 10;

  width: 30px;
  height: 30px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 0;

  color: #9baabd;

  background:
    rgba(255, 255, 255, 0.04);

  border:
    1px solid
    rgba(255, 255, 255, 0.055);

  border-radius: 9px;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}


.chat-button svg {
  width: 15px;
  height: 15px;
}


.chat-button:hover {
  color: #a9c9f5;

  background:
    rgba(90, 140, 210, 0.10);

  border-color:
    rgba(100, 150, 225, 0.12);

  transform:
    translateY(-1px);
}


html[data-theme="light"]
.chat-button {
  color: #728198;

  background:
    #f4f6f9;

  border-color:
    rgba(40, 65, 100, 0.055);
}


html[data-theme="light"]
.chat-button:hover {
  color: #3977cf;

  background:
    #edf3fb;

  border-color:
    rgba(55, 115, 205, 0.10);
}


/* ============================================================
   AVATAR
============================================================ */

.avatar-wrapper {
  position: relative;

  width: 88px;
  height: 88px;

  flex:
    0
    0
    88px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 3px;

  margin-top: 2px;

  background:
    #24272d;

  border:
    1px solid
    rgba(130, 175, 235, 0.20);

  border-radius: 50%;

  box-shadow:
    0
    9px
    24px
    rgba(0, 0, 0, 0.26),
    0
    0
    0
    3px
    rgba(85, 135, 205, 0.055);

  transition:
    border-color 0.20s ease,
    box-shadow 0.20s ease;
}


.avatar-wrapper:not(.viewed) {
  border-color:
    rgba(100, 160, 245, 0.50);

  box-shadow:
    0
    10px
    25px
    rgba(0, 0, 0, 0.27),
    0
    0
    0
    3px
    rgba(65, 135, 225, 0.09);
}


.avatar-wrapper.viewed {
  border-color:
    rgba(255, 255, 255, 0.075);

  box-shadow:
    0
    9px
    23px
    rgba(0, 0, 0, 0.22);
}


html[data-theme="light"]
.avatar-wrapper {
  background:
    #ffffff;

  border-color:
    rgba(60, 105, 165, 0.13);

  box-shadow:
    0
    9px
    22px
    rgba(15, 23, 42, 0.08),
    0
    0
    0
    3px
    rgba(60, 120, 205, 0.045);
}


html[data-theme="light"]
.avatar-wrapper:not(.viewed) {
  border-color:
    rgba(55, 125, 225, 0.40);
}


/* ============================================================
   AVATAR IMAGE
============================================================ */

.avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 50%;

  opacity: 0;

  transform:
    scale(0.97);

  transition:
    opacity 0.25s ease,
    transform 0.30s ease;
}


.avatar.loaded {
  opacity: 1;

  transform:
    scale(1);
}


/* ============================================================
   AVATAR SKELETON
============================================================ */

.avatar-skeleton {
  position: absolute;

  inset: 3px;

  overflow: hidden;

  background:
    #2b2e34;

  border-radius: 50%;
}


.avatar-skeleton::after {
  content: "";

  position: absolute;

  inset: 0;

  transform:
    translateX(-100%);

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.08),
      transparent
    );

  animation:
    avatar-shimmer
    1.25s
    ease-in-out
    infinite;
}


html[data-theme="light"]
.avatar-skeleton {
  background:
    #edf0f3;
}


html[data-theme="light"]
.avatar-skeleton::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.75),
      transparent
    );
}


@keyframes avatar-shimmer {

  to {
    transform:
      translateX(100%);
  }

}


/* ============================================================
   NEW / UNVIEWED DOT
============================================================ */

.status-dot {
  position: absolute;

  right: 10px;
  top: 8px;

  width: 10px;
  height: 10px;

  box-sizing: border-box;

  background:
    #4d98ef;

  border:
    2px solid
    #22242a;

  border-radius: 50%;

  box-shadow:
    0
    0
    0
    3px
    rgba(77, 152, 239, 0.10);
}


html[data-theme="light"]
.status-dot {
  border-color:
    #ffffff;
}


/* ============================================================
   INFO
============================================================ */

.user-info {
  width: 100%;

  min-width: 0;

  margin-top: 10px;

  display: flex;
  flex-direction: column;
  align-items: center;
}


/* ============================================================
   NAME
============================================================ */

.name {
  width: 100%;

  margin: 0;

  overflow: hidden;

  color: #f0f1f3;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;

  line-height: 1.2;

  letter-spacing: -0.25px;

  text-align: center;

  white-space: nowrap;

  text-overflow: ellipsis;
}


html[data-theme="light"]
.name {
  color: #33383f;
}


/* ============================================================
   AGE / META
============================================================ */

.age {
  max-width: 100%;

  margin:
    4px
    0
    0;

  overflow: hidden;

  color: #777e88;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.25;

  text-align: center;

  white-space: nowrap;

  text-overflow: ellipsis;
}


.age.is-company-age {
  color: #808894;

  font-size: 8px;

  letter-spacing: 0;

  text-transform: none;
}


html[data-theme="light"]
.age {
  color: #989ea6;
}


/* ============================================================
   SPECIFIC / COMPANY
============================================================ */

.specific {
  max-width: 148px;

  margin:
    6px
    0
    0;

  overflow: hidden;

  color: #9ca5b1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.25;

  text-align: center;

  display: -webkit-box;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow-wrap: anywhere;
}


.specific.is-specific {
  color: #9eabbc;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  letter-spacing: 0;

  line-height: 1.25;

  text-transform: none;
}


.specific.is-company {
  color: #9aaac0;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


html[data-theme="light"]
.specific {
  color: #7d858f;
}


html[data-theme="light"]
.specific.is-specific {
  color: #6e7c8f;
}


html[data-theme="light"]
.specific.is-company {
  color: #6b7e99;
}


/* ============================================================
   CONNECT BUTTON
============================================================ */

.connect-button {
  position: relative;

  z-index: 5;

  width: 100%;
  height: 31px;

  flex-shrink: 0;

  margin-top: auto;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  box-sizing: border-box;

  padding:
    0
    12px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3b86e8,
      #356fbd
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.075);

  border-radius: 9px;

  box-shadow:
    0
    7px
    16px
    rgba(40, 100, 185, 0.15);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
}


.button-icon {
  width: 13px;
  height: 13px;

  flex-shrink: 0;
}


.connect-button:not(.sent):not(.loading):hover {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    20px
    rgba(40, 100, 185, 0.22);
}


/* ============================================================
   SENT
============================================================ */

.connect-button.sent {
  color: #9eb9dd;

  background:
    rgba(90, 130, 180, 0.08);

  border:
    1px solid
    rgba(120, 155, 205, 0.12);

  box-shadow: none;
}


html[data-theme="light"]
.connect-button.sent {
  color: #57779f;

  background:
    #f1f5fa;

  border-color:
    rgba(65, 105, 160, 0.10);
}


/* ============================================================
   CONSULTANT
============================================================ */

.connect-button.ask {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #6256ad,
      #426fae
    );

  box-shadow:
    0
    7px
    16px
    rgba(80, 70, 160, 0.15);
}


/* ============================================================
   LOADING
============================================================ */

.connect-button.loading {
  cursor: default;

  opacity: 0.82;
}


.btn-spinner {
  width: 13px;
  height: 13px;

  box-sizing: border-box;

  border:
    2px solid
    rgba(255, 255, 255, 0.30);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    btn-spin
    0.65s
    linear
    infinite;
}


.connect-button.sent
.btn-spinner {
  border-color:
    rgba(130, 165, 210, 0.20);

  border-top-color:
    #9eb9dd;
}


@keyframes btn-spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .connect-cell,
  .chat-button,
  .avatar,
  .avatar-wrapper,
  .connect-button {
    transition: none;
  }


  .avatar-skeleton::after,
  .btn-spinner {
    animation: none;
  }

}

</style>